import React, { memo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button } from '@mui/material';
import { DataFieldCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import { crmContactApi } from 'app/services/api';
import PropTypes from 'prop-types';

import { FieldInputType } from 'constants/fields';

import { showErrorMessageAction } from 'store/global/actions';

import { BorrowersTestIds } from '../../../Borrowers.testIds';
import {
  buildCreateContactPayload,
  getContactIdValue,
  getCreateContactPrefill,
  isCreateContactValid,
  validateCreateContactForm,
} from '../../CrmContactSection.utils';

import { actionsStyles, formGridStyles, panelStyles } from './CreateContactPanel.styles';

const CreateContactPanel = ({ borrower, onCreated }) => {
  const dispatch = useDispatch();

  const [form, setForm] = useState(() => getCreateContactPrefill(borrower));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onFieldChange = useCallback(event => {
    const { name, value } = event.target;

    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const onSubmit = useCallback(async () => {
    const validationErrors = validateCreateContactForm(form);

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: createdContact } = await crmContactApi.createContact(
        buildCreateContactPayload(form),
      );

      onCreated({
        contactId: getContactIdValue(createdContact),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
      });
    } catch (err) {
      dispatch(showErrorMessageAction({ err }));
    } finally {
      setIsSubmitting(false);
    }
  }, [form, dispatch, onCreated]);

  return (
    <Box sx={panelStyles}>
      <Box sx={formGridStyles}>
        <TextFieldCDS
          required
          dataTestId={BorrowersTestIds.CREATE_CONTACT_PANEL_FIRST_NAME_INPUT}
          error={!!errors.firstName}
          helperText={errors.firstName}
          label="First name"
          name="firstName"
          placeholder="Jane"
          value={form.firstName}
          onChange={onFieldChange}
        />
        <TextFieldCDS
          required
          dataTestId={BorrowersTestIds.CREATE_CONTACT_PANEL_LAST_NAME_INPUT}
          error={!!errors.lastName}
          helperText={errors.lastName}
          label="Last name"
          name="lastName"
          placeholder="Cooper"
          value={form.lastName}
          onChange={onFieldChange}
        />
        <TextFieldCDS
          required
          dataTestId={BorrowersTestIds.CREATE_CONTACT_PANEL_EMAIL_INPUT}
          error={!!errors.primaryEmail}
          helperText={errors.primaryEmail}
          label="Email"
          name="primaryEmail"
          placeholder="jane@email.com"
          type="email"
          value={form.primaryEmail}
          onChange={onFieldChange}
        />
        <DataFieldCDS
          error={!!errors.primaryPhone}
          helperText={errors.primaryPhone}
          label="Phone"
          name="primaryPhone"
          placeholder="(555) 000-0000"
          type={FieldInputType.TEL_EXT}
          value={form.primaryPhone}
          onChange={onFieldChange}
        />
      </Box>

      <Box sx={actionsStyles}>
        <Button
          data-testid={BorrowersTestIds.CREATE_CONTACT_PANEL_SUBMIT_BUTTON}
          disabled={isSubmitting || !isCreateContactValid(form)}
          variant="contained"
          onClick={onSubmit}
        >
          Create &amp; link
        </Button>
      </Box>
    </Box>
  );
};

CreateContactPanel.propTypes = {
  borrower: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default memo(CreateContactPanel);
